/*
Arboretum screen — 2-level catalogue introduced in Android v1.1.0 (« Catalogue »).

Top-level toggle: Catalogue | Historique.
Under Catalogue, a second toggle: Par fréquence | Par genre.

- Catalogue Par fréquence : every identified species in the dataset
                            (excludes (G, sp.)), 3-column grid, sorted
                            by Pokédex rank `n` ascending. Captured →
                            cell with rank + thumb + display name,
                            clickable to species/{sk}. Non-captured →
                            silhouette `???` non-clickable.
- Catalogue Par genre     : same cells, grouped under alphabetical
                            genus headers. Header is clickable
                            (→ kPaths.genre(g)) when the user has at
                            least one capture of the genus, otherwise
                            a locked silhouette.
- Historique              : timeline of the user's captures grouped by
                            species, sorted by first-capture timestamp
                            descending (most-recent discovery first).
                            Card layout preserved from v1.0.x for
                            continuity with the pre-Phase-6 UX.

State persistence:
- sessionStorage 'arboretum.tab'  → 'catalogue' | 'historique'
- sessionStorage 'arboretum.sort' → 'frequence' | 'genre'

Data sources:
- speciesIndex.json   : Pokédex rank, vernacular name, unknown flag.
- species-info.json   : Paris-wide stats (used by the historique cards).
- genre-info.json     : genus French label (chapter headers).
- dataset-stats.json  : totalEspecesIdentifiees (catalogue denominator).
- captures (IDB)      : the user's captures, grouped by species for
                        the timeline and indexed by sk for thumbs.
*/

import { navigate, kPaths } from '../router'
import { groupCapturesBySpecies, type SpeciesCaptureGroup } from '../data/captureRepository'
import {
	ensureSpeciesIndex,
	speciesNameFor,
	allSpecies,
	displayName,
	formatLatin,
	isActiveSpecies,
	type SpeciesName,
} from '../data/speciesIndex'
import { ensureSpeciesInfo, speciesInfoFor } from '../data/speciesInfo'
import { ensureSpeciesPhotos, photosFor, photoUrl } from '../data/speciesPhotos'
import { ensureGenreInfo, genreInfoFor } from '../data/genreInfo'
import { ensureDatasetStats } from '../data/datasetStats'
import { loadPhoto } from '../data/photoStore'
import { mountTabSelector, type TabSelectorController } from './components/TabSelector'
import { renderIconPlataneSvg } from './components/IconPlatane'
import { renderEmptyState } from './components/EmptyState'
import { renderSkeletonBlock, renderSkeletonListCard } from './components/Skeleton'

// Date courte « 14/05/2026 » — parité Android DateFormat.SHORT.
const kFrenchDateShort = new Intl.DateTimeFormat('fr-FR')

type ArboretumTab = 'catalogue' | 'historique'
type CatalogueSort = 'frequence' | 'genre'

const kTabStorageKey = 'arboretum.tab'
const kSortStorageKey = 'arboretum.sort'

interface CatalogueEntry
{
	sk: number
	name: SpeciesName
	captured: boolean
	// Vignette : photo de référence de l'espèce (URL statique /data/) prioritaire
	// sur la 1re capture — parité Android CatalogueCell.kt / cellPhotos().
	// refPhotoUrl et thumbFilename sont mutuellement exclusifs : si la référence
	// existe, thumbFilename reste null pour que l'hydratation IDB saute la cellule.
	refPhotoUrl: string | null  // URL /data/species-photos de la principale, non-null seulement si capturée ET illustrée
	thumbFilename: string | null  // most-recent photo of the user's captures of this species, null when uncaptured/no-photo/ref-photo
}

interface ViewModel
{
	historiqueGroups: SpeciesCaptureGroup[]
	catalogueEntries: CatalogueEntry[]  // identified species only (excludes unknownSpecies)
	speciesIdentifiedTotal: number
	genresTotal: number
	genresDiscovered: number
	loadError: string | null
}

let fActiveObjectUrls: string[] = []  // photo Blob URLs created by the current mount, revoked on remount
let fTab1Ctrl: TabSelectorController | null = null  // level-1 tab selector, destroyed on remount
let fTab2Ctrl: TabSelectorController | null = null  // level-2 (Catalogue sort), destroyed on remount

/*
Mount the Arboretum screen. Reads the persisted tab/sort state, renders
the static shell, then hydrates async with captures + dataset.
*/
export function mountArboretumScreen(inRoot: HTMLElement): void
{
	revokeObjectUrls()
	destroyTabs()

	inRoot.innerHTML = renderShell()
	inRoot.querySelector('#nav-back')?.addEventListener('click', () => navigate(kPaths.map()))
	void hydrate(inRoot)
}

function revokeObjectUrls(): void
{
	for (const vUrl of fActiveObjectUrls) URL.revokeObjectURL(vUrl)
	fActiveObjectUrls = []
}

function destroyTabs(): void
{
	if (fTab1Ctrl) { fTab1Ctrl.destroy(); fTab1Ctrl = null }
	if (fTab2Ctrl) { fTab2Ctrl.destroy(); fTab2Ctrl = null }
}

function readTab(): ArboretumTab
{
	const vRaw = sessionStorage.getItem(kTabStorageKey)
	return vRaw === 'historique' ? 'historique' : 'catalogue'
}

function readSort(): CatalogueSort
{
	const vRaw = sessionStorage.getItem(kSortStorageKey)
	return vRaw === 'genre' ? 'genre' : 'frequence'
}

function writeTab(inTab: ArboretumTab): void
{
	try { sessionStorage.setItem(kTabStorageKey, inTab) } catch { /* private mode quota */ }
}

function writeSort(inSort: CatalogueSort): void
{
	try { sessionStorage.setItem(kSortStorageKey, inSort) } catch { /* private mode quota */ }
}

function renderShell(): string
{
	return `
		<div class="screen arboretum">
			<header class="arboretum-header">
				<button class="nav-back" id="nav-back" aria-label="Retour">←</button>
				<h1 class="display arboretum-title">Arboretum</h1>
			</header>
			<div class="arboretum-subheader" id="arboretum-subheader">
				${renderSkeletonBlock({ width: '220px', height: '14px' })}
			</div>
			<div class="arboretum-tabs" id="arboretum-tab1"></div>
			<div class="arboretum-tabs arboretum-tabs-secondary" id="arboretum-tab2"></div>
			<div class="arboretum-content" id="arboretum-content">
				${renderSkeletonListCard()}
				${renderSkeletonListCard()}
				${renderSkeletonListCard()}
			</div>
		</div>
	`
}

async function hydrate(inRoot: HTMLElement): Promise<void>
{
	revokeObjectUrls()

	const vModel = await loadViewModel()

	const vSubheader = inRoot.querySelector<HTMLElement>('#arboretum-subheader')
	const vTab1Host = inRoot.querySelector<HTMLElement>('#arboretum-tab1')
	const vTab2Host = inRoot.querySelector<HTMLElement>('#arboretum-tab2')
	const vContent = inRoot.querySelector<HTMLElement>('#arboretum-content')
	if (!vSubheader || !vTab1Host || !vTab2Host || !vContent) return

	if (vModel.loadError)
	{
		vSubheader.innerHTML = `<span class="arboretum-error">⚠ ${escapeHtml(vModel.loadError)}</span>`
		vContent.innerHTML = ''
		return
	}

	// HeaderCard primaryContainer 2 lignes — parité ArboretumScreen.kt.
	const vCapturedSpecies = vModel.historiqueGroups.length  // number of distinct identified+unknown species captured
	vSubheader.innerHTML = `
		<div class="arboretum-header-card">
			<div class="arboretum-header-card-title display">${vCapturedSpecies} / ${vModel.speciesIdentifiedTotal} espèces découvertes</div>
			<div class="arboretum-header-card-sub">${vModel.genresDiscovered} / ${vModel.genresTotal} genres découverts</div>
		</div>
	`

	// Level-1 tabs — Catalogue / Historique.
	let vActiveTab = readTab()
	fTab1Ctrl = mountTabSelector({
		host: vTab1Host,
		options: [
			{ id: 'catalogue', label: 'Catalogue' },
			{ id: 'historique', label: 'Historique' },
		],
		activeId: vActiveTab,
		onChange: (inId) => {
			vActiveTab = (inId === 'historique' ? 'historique' : 'catalogue')
			writeTab(vActiveTab)
			renderActiveTab(inRoot, vModel, vActiveTab)
		},
	})

	renderActiveTab(inRoot, vModel, vActiveTab)
}

/*
Render the content area + the secondary tab selector for the active
top-level tab. Called on initial hydration AND whenever the user
flips the level-1 selector.
*/
function renderActiveTab(inRoot: HTMLElement, inModel: ViewModel, inTab: ArboretumTab): void
{
	const vTab2Host = inRoot.querySelector<HTMLElement>('#arboretum-tab2')
	const vContent = inRoot.querySelector<HTMLElement>('#arboretum-content')
	if (!vTab2Host || !vContent) return

	// Destroy the previous level-2 controller — it's specific to the
	// catalogue tab.
	if (fTab2Ctrl) { fTab2Ctrl.destroy(); fTab2Ctrl = null }

	if (inTab === 'historique')
	{
		vTab2Host.style.display = 'none'
		vTab2Host.innerHTML = ''
		renderHistorique(vContent, inModel)
		return
	}

	// Catalogue tab — show level-2 tabs.
	vTab2Host.style.display = ''
	let vActiveSort = readSort()
	fTab2Ctrl = mountTabSelector({
		host: vTab2Host,
		options: [
			{ id: 'frequence', label: 'Par fréquence' },
			{ id: 'genre', label: 'Par genre' },
		],
		activeId: vActiveSort,
		onChange: (inId) => {
			vActiveSort = (inId === 'genre' ? 'genre' : 'frequence')
			writeSort(vActiveSort)
			renderCatalogue(vContent, inModel, vActiveSort)
		},
	})

	renderCatalogue(vContent, inModel, vActiveSort)
}

function renderHistorique(inHost: HTMLElement, inModel: ViewModel): void
{
	// Sort the user's species groups by most-recent first-capture
	// (= « le dernier découvert remonte »). Mirrors Android Historique
	// vue v1.1.0 — chronologie inversée vs l'ancienne PWA, alignée sur
	// l'attente : « j'ai capturé hier, je vois en premier ».
	const vSorted = inModel.historiqueGroups.slice().sort((inA, inB) => inB.firstCaptureAt - inA.firstCaptureAt)

	if (vSorted.length === 0)
	{
		// Libellés Android ArboretumEmptyState ; pas de CTA (parité stricte,
		// arbitrage 2026-06-10).
		inHost.innerHTML = renderEmptyState({
			variant: 'arboretum',
			title: 'Ton arboretum est vide.',
			body: "Approche-toi d'un arbre, tape son pin gris et capture-le pour révéler son espèce.",
		})
		return
	}

	const vCards = vSorted.map(renderHistoriqueCard).join('')
	inHost.innerHTML = `
		<div class="arboretum-list" role="list">
			<h2 class="arboretum-section-title">Espèces (${vSorted.length}/${inModel.speciesIdentifiedTotal})</h2>
			${vCards}
		</div>`
	inHost.querySelectorAll<HTMLElement>('.arboretum-card').forEach((inCard) => {
		inCard.addEventListener('click', () => {
			const vSk = Number(inCard.dataset.speciesIndex)
			if (Number.isFinite(vSk)) navigate(kPaths.species(vSk))
		})
	})
	void hydrateHistoriqueThumbnails(inHost, vSorted)
}

/*
Card Historique — parité Android SpeciesCard : thumbnail 72 px à gauche,
puis titre / binôme italique / « N arbres dans Paris » / « N photo(s) » /
« 1re capture : date courte ».
*/
function renderHistoriqueCard(inGroup: SpeciesCaptureGroup): string
{
	const vName = speciesNameFor(inGroup.speciesIndex)
	const vInfo = speciesInfoFor(inGroup.speciesIndex)
	const vTitle = vName ? displayName(vName) : 'Espèce inconnue'
	const vLatin = vName ? formatLatin(vName) : ''
	const vSubtitle = vTitle !== vLatin && vLatin.length > 0 ? vLatin : null
	const vParisCount = vInfo?.stats.count ?? null
	const vFirstDate = kFrenchDateShort.format(new Date(inGroup.firstCaptureAt))
	const vPhotoLabel = inGroup.captureCount === 1 ? '1 photo' : `${inGroup.captureCount} photos`

	return `
		<button class="arboretum-card" role="listitem" data-species-index="${inGroup.speciesIndex}">
			<div class="arboretum-card-thumb" aria-hidden="true"></div>
			<div class="arboretum-card-body">
				<div class="arboretum-card-title">${escapeHtml(vTitle)}</div>
				${vSubtitle ? `<div class="arboretum-card-subtitle"><em>${escapeHtml(vSubtitle)}</em></div>` : ''}
				${vParisCount !== null ? `<div class="arboretum-card-meta">${vParisCount} arbre${vParisCount > 1 ? 's' : ''} dans Paris</div>` : ''}
				<div class="arboretum-card-meta">${vPhotoLabel}</div>
				<div class="arboretum-card-foot">1re capture : ${vFirstDate}</div>
			</div>
		</button>
	`
}

async function hydrateHistoriqueThumbnails(inHost: HTMLElement, inGroups: SpeciesCaptureGroup[]): Promise<void>
{
	for (const vGroup of inGroups)
	{
		const vThumb = inHost.querySelector<HTMLElement>(
			`[data-species-index="${vGroup.speciesIndex}"] .arboretum-card-thumb`,
		)
		if (!vThumb) continue
		const vFile = vGroup.photoFilenames[0]  // most-recent first — parité Android group.captures.first()
		if (!vFile) continue
		const vBlob = await loadPhoto(vFile)
		if (!vBlob) continue
		const vUrl = URL.createObjectURL(vBlob)
		fActiveObjectUrls.push(vUrl)
		vThumb.style.backgroundImage = `url("${vUrl}")`
		vThumb.classList.add('arboretum-card-thumb-image')
	}
}

function renderCatalogue(inHost: HTMLElement, inModel: ViewModel, inSort: CatalogueSort): void
{
	if (inModel.catalogueEntries.length === 0)
	{
		inHost.innerHTML = `<div class="placeholder">Catalogue indisponible.</div>`
		return
	}

	if (inSort === 'frequence')
	{
		inHost.innerHTML = `<div class="genre-catalogue-grid arboretum-catalogue-grid">${
			inModel.catalogueEntries.map(renderCatalogueCell).join('')
		}</div>`
		wireCatalogueCells(inHost)
		void hydrateCatalogueThumbnails(inHost, inModel.catalogueEntries)
		return
	}

	// Par genre — group entries by latin genus, sort sections alphabetically.
	const vByGenre = new Map<string, CatalogueEntry[]>()
	for (const vE of inModel.catalogueEntries)
	{
		const vBucket = vByGenre.get(vE.name.genre) ?? []
		vBucket.push(vE)
		vByGenre.set(vE.name.genre, vBucket)
	}
	const vGenres = [...vByGenre.keys()].sort((inA, inB) => inA.localeCompare(inB, 'fr'))
	const vSections = vGenres.map((inGenre) => {
		const vBucket = vByGenre.get(inGenre) ?? []
		const vDiscovered = vBucket.some((inE) => inE.captured)
		const vGenreInfo = genreInfoFor(inGenre)
		const vFrLabel = vGenreInfo?.fr ?? null
		const vCells = vBucket.map(renderCatalogueCell).join('')
		const vCount = vBucket.filter((inE) => inE.captured).length
		const vTotal = vBucket.length
		const vHeader = vDiscovered ? `
			<button class="arboretum-section-header" data-genre="${escapeAttr(inGenre)}">
				<div class="arboretum-section-header-text">
					<span class="arboretum-section-header-title">${escapeHtml(vFrLabel ?? inGenre)}</span>
					${vFrLabel ? `<span class="arboretum-section-header-subtitle"><em>${escapeHtml(inGenre)}</em></span>` : ''}
				</div>
				<span class="arboretum-section-header-count">${vCount} / ${vTotal}</span>
				<span class="arboretum-section-header-chevron" aria-hidden="true">›</span>
			</button>
		` : `
			<div class="arboretum-section-header arboretum-section-header-locked" aria-disabled="true">
				<div class="arboretum-section-header-text">
					<span class="arboretum-section-header-title">???</span>
				</div>
				<span class="arboretum-section-header-count">0 / ${vTotal}</span>
			</div>
		`
		return `
			<section class="arboretum-catalogue-section">
				${vHeader}
				<div class="genre-catalogue-grid arboretum-catalogue-grid">${vCells}</div>
			</section>
		`
	}).join('')

	inHost.innerHTML = vSections
	wireCatalogueCells(inHost)
	inHost.querySelectorAll<HTMLElement>('.arboretum-section-header[data-genre]').forEach((inHeader) => {
		inHeader.addEventListener('click', () => {
			const vGenre = inHeader.dataset.genre
			if (vGenre) navigate(kPaths.genre(vGenre))
		})
	})
	void hydrateCatalogueThumbnails(inHost, inModel.catalogueEntries)
}

/*
Cell catalogue — parité Android CatalogueCell : rang « #019 » zéro-paddé
en tête, slot photo carré (silhouette « ? » sur fond surfaceContainerHighest
sinon), nom (ou « ??? »), binôme latin italique quand il diffère du titre.
*/
function renderCatalogueCell(inEntry: CatalogueEntry): string
{
	const vRank = inEntry.name.pokedexNumber !== null ? `#${String(inEntry.name.pokedexNumber).padStart(3, '0')}` : ''
	if (!inEntry.captured)
	{
		return `
			<div class="catalogue-cell catalogue-cell-locked" aria-disabled="true">
				<div class="catalogue-cell-rank">${vRank}</div>
				<div class="catalogue-cell-thumb catalogue-cell-thumb-locked" aria-hidden="true">?</div>
				<div class="catalogue-cell-name catalogue-cell-name-locked">???</div>
			</div>
		`
	}
	const vTitle = displayName(inEntry.name)
	const vLatin = formatLatin(inEntry.name)
	const vLatinHtml = vLatin && vLatin !== vTitle
		? `<div class="catalogue-cell-latin"><em>${escapeHtml(vLatin)}</em></div>`
		: ''
	// Priorité 1 (parité CatalogueCell.kt l.87-93) : photo de référence de
	// l'espèce, rendue DIRECTEMENT en background-image — même visuel final que
	// l'hydratation IDB (`.catalogue-cell-thumb-image` : background-size:cover),
	// mais URL statique sans étape async. Pas de data-thumb-filename → l'hydratation
	// saute la cellule (voir hydrateCatalogueThumbnails).
	// Priorité 2 : thumbnail 1re capture via l'hydratation IDB (placeholder + attribut).
	const vThumbHtml = inEntry.refPhotoUrl
		? `<div class="catalogue-cell-thumb catalogue-cell-thumb-image" style="background-image:url('${escapeAttr(inEntry.refPhotoUrl)}')" aria-hidden="true"></div>`
		: `<div class="catalogue-cell-thumb" data-thumb-filename="${escapeAttr(inEntry.thumbFilename ?? '')}" aria-hidden="true">${renderIconPlataneSvg('catalogue-cell-thumb-icon')}</div>`
	return `
		<button class="catalogue-cell" data-species-index="${inEntry.sk}" aria-label="${escapeAttr(vTitle)}">
			<div class="catalogue-cell-rank">${vRank}</div>
			${vThumbHtml}
			<div class="catalogue-cell-name">${escapeHtml(vTitle)}</div>
			${vLatinHtml}
		</button>
	`
}

function wireCatalogueCells(inHost: HTMLElement): void
{
	inHost.querySelectorAll<HTMLElement>('.catalogue-cell[data-species-index]').forEach((inCell) => {
		inCell.addEventListener('click', () => {
			const vSk = Number(inCell.dataset.speciesIndex)
			if (Number.isFinite(vSk)) navigate(kPaths.species(vSk))
		})
	})
}

async function hydrateCatalogueThumbnails(inHost: HTMLElement, inEntries: CatalogueEntry[]): Promise<void>
{
	// Catalogue can be very wide (~784 cells at v1.3.2) — hydrating thumbs is O(N
	// IDB reads) and may noticeably stall the main thread on cold cache.
	// We chunk through entries and yield after each successful blob load
	// to keep the input responsive while the grid fills in.
	for (const vEntry of inEntries)
	{
		if (!vEntry.captured || !vEntry.thumbFilename) continue
		const vCell = inHost.querySelector<HTMLElement>(`.catalogue-cell[data-species-index="${vEntry.sk}"] .catalogue-cell-thumb`)
		if (!vCell) continue
		const vBlob = await loadPhoto(vEntry.thumbFilename)
		if (!vBlob) continue
		const vUrl = URL.createObjectURL(vBlob)
		fActiveObjectUrls.push(vUrl)
		vCell.style.backgroundImage = `url("${vUrl}")`
		vCell.classList.add('catalogue-cell-thumb-image')
		vCell.textContent = ''
	}
}

async function loadViewModel(): Promise<ViewModel>
{
	try
	{
		// ensureSpeciesPhotos ne throw jamais (asset absent → photosFor null
		// partout, retombée sur les thumbs capture — parité SpeciesPhotoRepository).
		const [, , , vGenreMap, vStats, vGroups] = await Promise.all([
			ensureSpeciesIndex(),
			ensureSpeciesInfo(),
			ensureSpeciesPhotos(),
			ensureGenreInfo(),
			ensureDatasetStats(),
			groupCapturesBySpecies(),
		])

		// Catalogue: every active species (v1.3.1 isActive filter —
		// !unknownSpecies AND pokedexNumber != null). The pokedex filter
		// purges ~16 zombie rows (identified species with count=0 in the
		// current OpenData snapshot, e.g. Eriolobus trilobata) that
		// previously bloated the grid with "—" cells unreachable to any
		// user. Sort by Pokédex rank ascending; rows without a rank
		// sink to the bottom (rare now that the filter excludes them).
		const vIdentified = allSpecies().filter((inE) => isActiveSpecies(inE.name))
		const vCapturedSk = new Set<number>()  // speciesIndex with at least one capture
		const vMostRecentFile = new Map<number, string>()  // speciesIndex -> most-recent photoFilename, for thumbs
		for (const vGroup of vGroups)
		{
			vCapturedSk.add(vGroup.speciesIndex)
			const vFile = vGroup.photoFilenames[0]  // photoFilenames is already most-recent first
			if (vFile) vMostRecentFile.set(vGroup.speciesIndex, vFile)
		}

		// Décision de vignette au niveau du view model (parité cellPhotos Android) :
		// photosFor n'est consulté QUE pour les espèces capturées — anti-spoiler
		// structurel, aucune URL de référence n'existe dans le DOM d'une espèce
		// non capturée (donc aucune requête réseau spoilable).
		// Perf : seules les dizaines d'espèces capturées portent une URL (WebP
		// ~60 Ko, SW cache-first /data/), pas les ~784 cellules du catalogue —
		// pas besoin d'IntersectionObserver ni de hydratation lazy.
		const vCatalogueEntries: CatalogueEntry[] = vIdentified.map((inE) => {
			const vCaptured = vCapturedSk.has(inE.sk)
			const vRefPhotos = vCaptured ? photosFor(inE.sk) : null
			const vRefUrl = vRefPhotos ? photoUrl(vRefPhotos.principal) : null
			return {
				sk: inE.sk,
				name: inE.name,
				captured: vCaptured,
				refPhotoUrl: vRefUrl,
				// Mutuellement exclusif avec refPhotoUrl : si une réf existe, on
				// laisse thumbFilename null pour que l'hydratation IDB saute la cellule.
				thumbFilename: vRefUrl ? null : (vMostRecentFile.get(inE.sk) ?? null),
			}
		})
		vCatalogueEntries.sort((inA, inB) => {
			const vRa = inA.name.pokedexNumber ?? Number.MAX_SAFE_INTEGER
			const vRb = inB.name.pokedexNumber ?? Number.MAX_SAFE_INTEGER
			if (vRa !== vRb) return vRa - vRb
			return formatLatin(inA.name).localeCompare(formatLatin(inB.name), 'fr')
		})

		// Genres discovered = distinct latin genus across all the user's
		// captures (including unknownSpecies — a single (Acer, sp.)
		// capture still unlocks the Acer chapter header).
		const vGenresDiscovered = new Set<string>()
		for (const vGroup of vGroups)
		{
			const vName = speciesNameFor(vGroup.speciesIndex)
			if (vName) vGenresDiscovered.add(vName.genre)
		}

		const vSpeciesIdentifiedTotal = vStats.totalEspecesIdentifiees ?? vIdentified.length

		return {
			historiqueGroups: vGroups,
			catalogueEntries: vCatalogueEntries,
			speciesIdentifiedTotal: vSpeciesIdentifiedTotal,
			genresTotal: vGenreMap.size,
			genresDiscovered: vGenresDiscovered.size,
			loadError: null,
		}
	}
	catch (vErr)
	{
		const vMsg = vErr instanceof Error ? vErr.message : 'Erreur inconnue'
		console.error('Arboretum hydration failed', vErr)
		return {
			historiqueGroups: [],
			catalogueEntries: [],
			speciesIdentifiedTotal: 0,
			genresTotal: 0,
			genresDiscovered: 0,
			loadError: vMsg,
		}
	}
}

function escapeHtml(inText: string): string
{
	return inText
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}

function escapeAttr(inText: string): string { return escapeHtml(inText) }
